"use client";

import {
	RiEyeFill,
	RiFlagFill,
	RiHeartFill,
	RiLiveFill,
	RiMicFill,
	RiStarFill,
	RiStarLine,
} from "@remixicon/react";
import type { ProcessedSet, Review } from "@workspace/types";
import {
	isProgramSet,
	parseGenres,
	parseSpecialGuestsForBadges,
	setHasSpecialGuests,
} from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo } from "react";
import { GenreBadgeList } from "@/components/shared/genre-badge-list";
import { SetInfoDisplay } from "@/components/shared/set-info-display";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { isSetPast } from "@/lib/utils/date/formatting";

// Utility function to extract YouTube video ID from various URL formats
const extractYouTubeVideoId = (url: string): string | null => {
	const patterns = [
		/youtu\.be\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match?.[1]) return match[1];
	}
	return null;
};

interface ReadOnlyStarsProps {
	rating: number;
	loved?: boolean;
	className?: string;
}

const ReadOnlyStars = ({
	rating,
	loved = false,
	className,
}: ReadOnlyStarsProps) => {
	return (
		<div className={cn("flex items-center gap-2", className)}>
			{/* Stars */}
			<div className="flex items-center gap-0.5">
				{[1, 2, 3, 4, 5].map((starValue) => {
					const isFilled = starValue <= rating;
					return (
						<div key={starValue} className="relative">
							{isFilled ? (
								<RiStarFill
									className="h-6 w-6 text-yellow-500 transition-all duration-100"
									size={24}
								/>
							) : (
								<RiStarLine
									className="h-6 w-6 text-gray-300 transition-all duration-100"
									size={24}
								/>
							)}
						</div>
					);
				})}
			</div>

			{/* Heart (only when loved) */}
			{loved && (
				<div className="ml-2">
					<RiHeartFill className="h-6 w-6 text-red-500" />
				</div>
			)}
		</div>
	);
};

interface SetAnonModalProps {
	isOpen: boolean;
	set: ProcessedSet | null;
	review: Review | null;
	onClose: () => void;
	onStartReview?: (setId: string) => void;
	onAuthRequired?: (message?: string) => void;
	festivalSlug?: string;
}

export const SetAnonModal = memo<SetAnonModalProps>(
	({
		isOpen,
		set,
		review,
		onClose,
		onAuthRequired,
		festivalSlug,
	}: SetAnonModalProps) => {
		if (!set) return null;

		// Check if this is a program set
		const isProgram = isProgramSet(set);
		const videoId = set.youtube_url
			? extractYouTubeVideoId(set.youtube_url)
			: null;

		return (
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
					<DialogTitle className="sr-only">Set Details</DialogTitle>
					<DialogDescription className="sr-only">
						View set details and information
					</DialogDescription>
					<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
						{/* Use SetInfoDisplay component for artist photo and info */}
						<SetInfoDisplay
							set={set}
							context="modal"
							titleElement="div"
							titleClassName="font-semibold text-lg leading-tight"
							photoSize="lg"
							photoRounded="lg"
							showStage={true}
							showTime={true}
							showLiveIndicator={false}
							showGenres={false}
							photoType="artist"
						/>
					</DialogHeader>

					<div className="space-y-6 px-6">
						{/* Genres section - moved from header */}
						{(() => {
							// Get genres - only for artist sets, not program sets
							let genreArray: string[] = [];

							if (!isProgram) {
								if (set.is_collaborative && set.set_artists) {
									const allGenres = new Set<string>();
									for (const setArtist of set.set_artists) {
										if (setArtist.artist.spotify_genres) {
											const genres = parseGenres(
												setArtist.artist.spotify_genres,
												setArtist.artist.manual_genres,
											);
											for (const genre of genres) {
												allGenres.add(genre);
											}
										}
									}
									genreArray = Array.from(allGenres);
								} else if (set.artist?.spotify_genres) {
									genreArray = parseGenres(
										set.artist.spotify_genres,
										set.artist.manual_genres,
									);
								}
							}

							const hasGenres = genreArray.length > 0;

							// Don't render anything if no genres
							if (!hasGenres) return null;

							return (
								<div className="space-y-1">
									<Label>Genres</Label>
									<GenreBadgeList genres={genreArray} className="gap-2" />
								</div>
							);
						})()}

						{/* Special Guests section - moved from header */}
						{!isProgram && setHasSpecialGuests(set) && (
							<div className="space-y-1">
								<Label>Special Guests</Label>
								<div className="flex flex-wrap gap-1">
									{parseSpecialGuestsForBadges(set.special_guests || null).map(
										(guest) => (
											<Link
												key={guest}
												href={`/artists/${createArtistSlug(guest)}`}
												className="cursor-pointer rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors duration-200 hover:bg-primary/20"
											>
												{guest}
											</Link>
										),
									)}
								</div>
							</div>
						)}
						{/* Video Player */}
						{videoId && (
							<div className="aspect-video w-full overflow-hidden rounded-lg">
								<iframe
									src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`}
									title="YouTube video player"
									style={{ border: 0 }}
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
									allowFullScreen
									className="h-full w-full"
								/>
							</div>
						)}

						{/* Review section - show for all sets with reviews */}
						{review && (
							<div className="space-y-4">
								{/* Stars and Heart display */}
								{review.rating > 0 && (
									<div className="flex justify-center">
										<ReadOnlyStars
											rating={review.rating}
											loved={review.loved}
										/>
									</div>
								)}

								{/* Status indicators */}
								<div className="flex flex-wrap justify-center gap-2">
									{review.wantToSee && (
										<div className="flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 text-blue-800 text-xs">
											<RiEyeFill className="h-3 w-3" />
											<span>
												{isSetPast(set) ? "Wanted to See" : "Want to See"}
											</span>
										</div>
									)}
									{review.mustSee && (
										<div className="flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-purple-800 text-xs">
											<RiFlagFill className="h-3 w-3" />
											<span>Must See</span>
										</div>
									)}
									{review.sawLive && (
										<div className="flex items-center gap-1 rounded-lg bg-green-100 px-2 py-1 text-green-800 text-xs">
											<RiMicFill className="h-3 w-3" />
											<span>Saw Live</span>
										</div>
									)}
									{review.sawStream && (
										<div className="flex items-center gap-1 rounded-lg bg-red-100 px-2 py-1 text-red-800 text-xs">
											<RiLiveFill className="h-3 w-3" />
											<span>Saw Stream</span>
										</div>
									)}
								</div>

								{/* Review content */}
								{review.content?.trim() && (
									<div className="space-y-2">
										<Label>Notes</Label>
										<div className="rounded-lg bg-muted p-3 text-sm">
											{review.content}
										</div>
									</div>
								)}
							</div>
						)}

						{/* Program set description */}
						{isProgram &&
							"_program_set" in set &&
							set._program_set?.description && (
								<div className="space-y-2">
									<Label>Description</Label>
									<div className="rounded-lg bg-muted p-3 text-sm">
										{set._program_set.description}
									</div>
								</div>
							)}
					</div>
					<DialogFooter className="px-6 pt-0 pb-6">
						<div className="flex w-full items-center justify-between">
							{/* View Details button - left side */}
							{festivalSlug && (
								<Button variant="secondary" asChild>
									<Link href={`/festivals/${festivalSlug}/${set.id}`}>
										View Details
									</Link>
								</Button>
							)}
							{!festivalSlug && <div />}{" "}
							{/* Spacer when no View Details button */}
							{/* CTA button - right side */}
							{onAuthRequired && (
								<Button
									onClick={() => {
										onClose();
										onAuthRequired(
											review
												? "Sign in to edit your review"
												: isProgram
													? "Sign in to add thoughts on this program"
													: !set.start_time // Lineup-only festival (no schedule)
														? "Sign in to add this set to your list"
														: isSetPast(set)
															? "Sign in to add a review"
															: "Sign in to save this set to your list",
										);
									}}
								>
									{review
										? "Edit Review"
										: isProgram
											? "Add Thoughts"
											: !set.start_time // Lineup-only festival (no schedule)
												? "Add to List"
												: isSetPast(set)
													? "Add Review"
													: "Save to List"}
								</Button>
							)}
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	},
);

SetAnonModal.displayName = "SetAnonModal";
